import React, { useState } from 'react';
import { IndianRupee, Printer, Loader2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Segmented } from '@/components/ui/segmented';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { usePermission } from '@/hooks/usePermission';
import { invoiceStatus, paidVia } from './InvoiceTable';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { StatusChip } from '@/components/ui/status-chip';
import { RefundModal } from './RefundModal';
import { cn } from '@/lib/utils';
import { getPatientDeposits, recordInvoicePayment } from '@/lib/api';
// Import centralized types
import type { Invoice } from '@/types';

interface PaymentRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onRecordPayment: (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => void;
  loading?: boolean;
}

export function PaymentRecordModal({
                                     isOpen,
                                     onClose,
                                     invoice,
                                     onRecordPayment,
                                     loading = false
                                   }: PaymentRecordModalProps) {
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [upiApp, setUpiApp] = useState('');
  const [bankName, setBankName] = useState('');
  const { formatCurrency } = useBillingUtils();
  // The patient's advance (deposits not yet used) — offered as a way to pay this bill
  const [advance, setAdvance] = useState(0);
  // Part of the bill paid from the advance; the rest (paymentAmount) is paid by the method below
  const [fromAdvance, setFromAdvance] = useState(0);
  const [advanceSaving, setAdvanceSaving] = useState(false);
  const [error, setError] = useState('');

  // Update payment amount when invoice changes
  React.useEffect(() => {
    if (invoice) {
      setPaymentAmount(Number(invoice.balance_due) || 0);
    }
  }, [invoice]);

  React.useEffect(() => {
    setAdvance(0);
    setFromAdvance(0);
    setError('');
    if (!isOpen || !invoice?.patient_id || !invoice?.clinic_id) return;
    let live = true;
    getPatientDeposits(invoice.patient_id, invoice.clinic_id)
      .then(d => { if (live) setAdvance(Number(d?.available_advance || 0)); })
      .catch(() => { /* no advance offered */ });
    return () => { live = false; };
  }, [isOpen, invoice?.patient_id, invoice?.clinic_id]);

  const due = Number(invoice?.balance_due) || 0;
  const rest = Math.round((due - fromAdvance) * 100) / 100;   // left to pay once the advance is used
  const total = Math.round((fromAdvance + paymentAmount) * 100) / 100;
  const busy = loading || advanceSaving;

  const handleSubmit = async () => {
    if (!invoice || total <= 0 || paymentAmount > rest) return;
    const meta = {
      reference_number: referenceNumber || undefined,
      upi_app:          upiApp          || undefined,
      bank_name:        bankName        || undefined,
    };
    // All from the advance: one payment, saved the usual way
    if (paymentAmount <= 0) { onRecordPayment(fromAdvance, 'advance'); return; }
    // Advance and another method: the advance part first, then the rest the usual way.
    // If the rest fails, the advance stays applied and the bill shows what's still due.
    if (fromAdvance > 0) {
      setAdvanceSaving(true); setError('');
      try {
        await recordInvoicePayment(invoice.id, { amount: fromAdvance, payment_mode: 'advance', payment_date: new Date().toISOString() });
      } catch (e: any) {
        setError(e?.message || 'Could not use the advance'); return;
      } finally { setAdvanceSaving(false); }
    }
    onRecordPayment(paymentAmount, paymentMethod, meta);
  };

  const handleClose = () => {
    setPaymentAmount(0);
    setFromAdvance(0);
    setError('');
    setPaymentMethod('cash');
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
    onClose();
  };

  // Advance on account: one click, as in the New bill dialog — not a hidden payment mode.
  // Whatever it doesn't cover is paid the usual way in the same step.
  const useAdvance = () => { const a = Math.min(due, advance); setFromAdvance(a); setPaymentAmount(Math.round((due - a) * 100) / 100); };
  const stopUsingAdvance = () => { setFromAdvance(0); setPaymentAmount(due); };

  const handleMethodChange = (method: string) => {
    setPaymentMethod(method);
    // Clear method-specific fields when switching
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
  };

  return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
            <DialogTitle className="text-base">Record payment</DialogTitle>
            <DialogDescription className="text-xs">
              {[invoice?.patient_name, invoice?.invoice_number].filter(Boolean).join(' · ')}
            </DialogDescription>
          </DialogHeader>

          {invoice && (
              <div className="px-5 py-4 space-y-4 flex-1 min-h-0 overflow-y-auto">
                {/* Invoice summary */}
                <div className="rounded-md bg-muted/40 px-3 py-2.5 text-sm space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-label">Bill total</span>
                    <span className="font-mono tabular-nums text-foreground">{formatCurrency(invoice.total_amount)}</span>
                  </div>
                  {invoice.paid_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-label">Paid so far</span>
                        <span className="font-mono tabular-nums text-foreground">{formatCurrency(invoice.paid_amount)}</span>
                      </div>
                  )}
                  <div className="flex justify-between border-t border-border pt-1.5">
                    <span className="font-medium text-foreground">Due</span>
                    <span className="font-mono font-semibold tabular-nums text-warning">
                      {formatCurrency(invoice.balance_due)}
                    </span>
                  </div>
                </div>

                {/* The patient's advance: one row with Use, as in the New bill dialog */}
                {advance > 0 && (
                    <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2">
                      <span className="flex-1 text-sm text-label">
                        {fromAdvance > 0 ? 'From advance' : 'Advance'}{' '}
                        <span className="font-mono font-semibold text-foreground">{formatCurrency(fromAdvance > 0 ? fromAdvance : advance)}</span>
                      </span>
                      {fromAdvance > 0 ? (
                          <Button type="button" size="sm" variant="ghost" className="shrink-0" onClick={stopUsingAdvance}>Don't use</Button>
                      ) : (
                          <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={useAdvance}>
                            Use {formatCurrency(Math.min(due, advance))}
                          </Button>
                      )}
                    </div>
                )}

                {/* Payment amount — once the advance is used, the rest still to pay */}
                {rest > 0 && (<>
                <div>
                  <label htmlFor="payment-amount" className="block text-[13px] font-semibold text-label mb-1">
                    {fromAdvance > 0 ? 'Rest, paid now' : 'Amount'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">₹</span>
                    <Input
                        id="payment-amount"
                        type="number"
                        min="0"
                        max={rest}
                        step="0.01"
                        inputMode="decimal"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                        placeholder="Enter payment amount"
                        className="pl-7 font-mono tabular-nums text-right"
                    />
                  </div>
                  {paymentAmount > rest ? (
                      <p className="mt-1 text-2xs text-destructive">More than the {formatCurrency(rest)} {fromAdvance > 0 ? 'left after the advance' : 'due'}</p>
                  ) : fromAdvance > 0 && paymentAmount > 0 && paymentAmount < rest ? (
                      <p className="mt-1 text-2xs text-muted-foreground">{formatCurrency(rest - paymentAmount)} stays due on this bill</p>
                  ) : null}
                </div>

                {/* Payment method — the same switch as the New bill dialog */}
                {paymentAmount > 0 && (
                <div>
                  <span className="block text-[13px] font-semibold text-label mb-1">Paid by</span>
                  <Segmented
                      aria-label="Payment method"
                      className="flex w-full"
                      options={[
                        { value: 'cash', label: 'Cash' },
                        { value: 'upi', label: 'UPI' },
                        { value: 'card', label: 'Card' },
                        { value: 'cheque', label: 'Cheque' },
                        { value: 'netbanking', label: 'Bank', title: 'Bank transfer (NEFT / RTGS / IMPS)' },
                        { value: 'other', label: 'Other', title: 'Insurance or other' },
                      ]}
                      value={paymentMethod}
                      onChange={handleMethodChange}
                  />
                </div>
                )}
                </>)}

                {/* UPI app selector — shown only for upi */}
                {paymentMethod === 'upi' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[13px] font-semibold text-label mb-1">
                          UPI app
                        </label>
                        <Select value={upiApp} onValueChange={setUpiApp}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select app (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gpay">Google Pay</SelectItem>
                            <SelectItem value="phonepe">PhonePe</SelectItem>
                            <SelectItem value="paytm">Paytm</SelectItem>
                            <SelectItem value="bhim">BHIM</SelectItem>
                            <SelectItem value="other">Other UPI</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-label mb-1">
                          UPI transaction ID
                        </label>
                        <Input
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            placeholder="e.g. 123456789012"
                            className="font-mono text-sm"
                        />
                      </div>
                    </div>
                )}

                {/* Cheque details */}
                {paymentMethod === 'cheque' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[13px] font-semibold text-label mb-1">
                          Cheque no.
                        </label>
                        <Input
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            placeholder="Enter cheque number"
                            className="font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-label mb-1">
                          Bank name
                        </label>
                        <Input
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            placeholder="e.g. SBI, HDFC, ICICI"
                        />
                      </div>
                    </div>
                )}

                {/* Bank transfer details */}
                {paymentMethod === 'netbanking' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[13px] font-semibold text-label mb-1">
                          Transaction reference
                        </label>
                        <Input
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            placeholder="NEFT/RTGS reference number"
                            className="font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-label mb-1">
                          Bank name
                        </label>
                        <Input
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            placeholder="e.g. SBI, HDFC, ICICI"
                        />
                      </div>
                    </div>
                )}

                {/* Card — last 4 digits */}
                {paymentMethod === 'card' && (
                    <div>
                      <label className="block text-[13px] font-semibold text-label mb-1">
                        Card last 4 digits
                      </label>
                      <Input
                          value={referenceNumber}
                          onChange={(e) => setReferenceNumber(e.target.value.replace(/\D/g, '').slice(0, 4))}
                          placeholder="e.g. 4321"
                          maxLength={4}
                          className="font-mono text-sm w-24"
                      />
                    </div>
                )}

                {error && <p className="text-xs text-destructive">{error}</p>}
              </div>
          )}

          <div data-kb-footer className="flex justify-end gap-2 px-5 py-3 border-t border-border">
            <Button
                variant="ghost"
                onClick={handleClose}
                disabled={busy}
            >
              Cancel
            </Button>
            <Button
                onClick={handleSubmit}
                disabled={busy || total <= 0 || paymentAmount < 0 || paymentAmount > rest}
                className="min-w-32"
            >
              {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                  `Record ${formatCurrency(total)}`
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}

interface InvoiceViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onPrint: (invoice: Invoice) => void;
  onPayment?: (invoice: Invoice) => void;
  onRefunded?: () => void;
  showPatientInfo?: boolean;
  variant?: 'patient' | 'clinic';
}

export function InvoiceViewModal({
                                   isOpen,
                                   onClose,
                                   invoice,
                                   onPrint,
                                   onPayment,
                                   onRefunded,
                                   showPatientInfo = true,
                                   variant = 'patient'
                                 }: InvoiceViewModalProps) {
  const { formatCurrency, formatDate } = useBillingUtils();
  const { can } = usePermission();
  const [showRefundModal, setShowRefundModal] = useState(false);

  const isCancelled = invoice?.payment_status === 'cancelled';
  const canAcceptPayment = !isCancelled && invoice && invoice.balance_due > 0 && onPayment;
  // Refunds are allowed on cancelled invoices too — "cancel, then return the money"
  // is the most common real-world refund trigger.
  const canRefund = can('billing:refund') && invoice && Number(invoice.paid_amount) > 0;

  return (
      <>
      {/* Details open in a side panel so the list stays in view */}
      <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent side="right" className="w-full sm:max-w-[440px] p-0 gap-0 flex flex-col">
          <div className="px-4 py-3 pr-12 border-b border-border">
            <SheetTitle className="text-base font-semibold flex items-center gap-2">
              <span className={cn("font-mono", isCancelled && "line-through decoration-1 text-muted-foreground")}>{invoice?.invoice_number ?? "Invoice"}</span>
              {invoice && <StatusChip tone={invoiceStatus(invoice).tone}>{invoiceStatus(invoice).label}</StatusChip>}
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              {invoice ? [formatDate(invoice.invoice_date), invoice.doctor_name, invoice.clinic_name].filter(Boolean).join(" · ") : "Invoice details"}
            </SheetDescription>
          </div>

          {invoice && (
              <div className="flex-1 overflow-y-auto divide-y divide-border">
                {isCancelled && (
                    // Grey, not red — a cancelled bill needs no action
                    <p className="px-4 py-3 text-sm text-muted-foreground">This invoice was cancelled. No further payments can be recorded.</p>
                )}

                {showPatientInfo && (
                    <section className="px-4 py-3">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Patient</h3>
                      <p className="text-sm font-medium text-foreground">{invoice.patient_name || '—'}</p>
                      {invoice.patient_phone && <p className="text-xs text-muted-foreground tabular-nums">{invoice.patient_phone}</p>}
                    </section>
                )}

                {invoice.items && invoice.items.length > 0 && (
                    <section className="px-4 py-3">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Services</h3>
                      <ul>
                        {invoice.items.map((item, index) => (
                            <li key={index} className="flex justify-between gap-3 text-sm py-1.5 border-b border-divider last:border-0">
                              <span className="min-w-0 text-foreground">
                                {item.service_name}
                                {item.quantity > 1 && <span className="text-muted-foreground ml-1.5 text-xs">× {item.quantity}</span>}
                              </span>
                              <span className="font-mono tabular-nums text-foreground">{formatCurrency(item.total_price || item.unit_price || 0)}</span>
                            </li>
                        ))}
                      </ul>
                    </section>
                )}

                <section className="px-4 py-3 space-y-1.5 text-sm">
                  {Number(invoice.discount_amount) > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span className="font-mono tabular-nums">{formatCurrency(Number(invoice.subtotal ?? invoice.total_amount))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Discount</span>
                          <span className="font-mono tabular-nums text-success">− {formatCurrency(Number(invoice.discount_amount))}</span>
                        </div>
                      </>
                  )}
                  {Number(invoice.tax_amount) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax</span>
                        <span className="font-mono tabular-nums">+ {formatCurrency(Number(invoice.tax_amount))}</span>
                      </div>
                  )}
                  <div className="flex justify-between text-base pt-1">
                    <span className="font-semibold text-foreground">Total</span>
                    <span className={cn("font-mono font-semibold tabular-nums", isCancelled ? "text-muted-foreground line-through" : "text-foreground")}>
                      {formatCurrency(Number(invoice.total_amount))}
                    </span>
                  </div>
                  {!isCancelled && Number(invoice.paid_amount) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Paid{paidVia(invoice) && ` · ${paidVia(invoice)}`}</span>
                        <span className="font-mono tabular-nums text-success">{formatCurrency(Number(invoice.paid_amount))}</span>
                      </div>
                  )}
                  {Number(invoice.refunded_amount) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Refunded</span>
                        <span className="font-mono tabular-nums text-destructive">{formatCurrency(Number(invoice.refunded_amount))}</span>
                      </div>
                  )}
                  {!isCancelled && Number(invoice.balance_due) > 0 && (
                      <div className="flex justify-between items-center border-t border-border pt-2 mt-1">
                        <span className="font-medium text-foreground">Balance due</span>
                        <span className="rounded bg-highlight px-2 py-0.5 font-mono font-semibold tabular-nums text-highlight-foreground">
                          {formatCurrency(Number(invoice.balance_due))}
                        </span>
                      </div>
                  )}
                </section>
              </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-border bg-card">
            {/* Refund — for paid or partially paid invoices, cancelled ones too */}
            {canRefund && (
                <Button variant="outline" size="sm" className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10 mr-auto" onClick={() => setShowRefundModal(true)}>
                  <Undo2 className="w-3.5 h-3.5" /> Refund
                </Button>
            )}
            {/* Print — allowed even on cancelled invoices for records */}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => invoice && onPrint(invoice)}>
              <Printer className="w-3.5 h-3.5" /> Print
            </Button>
            {/* Record Payment — hidden for cancelled invoices */}
            {canAcceptPayment && (
                <Button size="sm" onClick={() => { onClose(); onPayment!(invoice!); }} className="gap-1.5">
                  <IndianRupee className="w-3.5 h-3.5" /> Record payment
                </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <RefundModal
          isOpen={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          invoice={invoice}
          onRefunded={onRefunded}
      />
      </>
  );
}